import { EndpointMapping } from "@/types";

export const normPath = (p?: string) => {
  let v = (p || "/").trim();
  if (!v.startsWith("/")) v = "/" + v;
  v = v.replace(/\/{2,}/g, "/");
  return v.length > 1 && v.endsWith("/") ? v.slice(0, -1) : v;
};

export const normSub = (s?: string) => (s || "").trim().toLowerCase();

export const RESERVED = ["/public", "/assets", "/health", "/static"];

/** Перший сегмент шляху (після нормалізації): domain.com/static/foo → "static", domain.com/get/static/foo → "get" */
function getFirstPathSegment(path: string): string {
  const normalized = normPath(path || "/");
  const withoutLeading = normalized.replace(/^\/+/, "");
  return withoutLeading.split("/")[0] ?? "";
}

/**
 * Блокує тільки якщо зарезервоване слово йде одразу після домену (перший сегмент).
 * domain.com/static/... → блок, domain.com/get/static/... → дозволено.
 */
export function isPathReserved(path: string): boolean {
  const firstSegment = getFirstPathSegment(path);
  if (!firstSegment) return false;
  const reservedSegments = RESERVED.map((p) => p.replace(/^\/+/, ""));
  return reservedSegments.includes(firstSegment);
}

export const validatePathSyntax = (path: string) => {
  if (!path.startsWith("/")) return "Шлях повинен починатися з '/'";
  const body = path.slice(1);
  if (!body) return null;
  // {{macro}} допустимы
  const macro = /\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/g;
  const cleaned = body.replace(macro, "PARAM");
  if (cleaned.includes("{") || cleaned.includes("}")) {
    return "Макроси повинні бути у форматі {{param_name}}";
  }
  if (!/^[a-zA-Z0-9\-\/PARAM]+$/.test(cleaned)) {
    return "Допустимі символи: латиниця, цифри, '-', '/', та макроси {{param}}";
  }
  return null;
};

/** Витягує response з помилки (підтримує enhanced error з api.interceptor, де originalError зберігає axios error). */
function getResponse(err: any): any {
  return err?.originalError?.response ?? err?.response;
}

/**
 * Витягує текст помилки з відповіді бекенду або з об'єкта помилки.
 * Підтримує: message (string | string[]), error (string), errors (array), enhanced Error з originalError.
 */
export function extractApiError(err: any): string {
  const response = getResponse(err);
  const data = response?.data;

  if (data != null && typeof data === "object") {
    const msg = data.message;
    if (Array.isArray(msg) && msg.length > 0) {
      return msg.map((m: any) => (typeof m === "string" ? m : m?.message ?? String(m))).join("\n");
    }
    if (typeof msg === "string" && msg.trim()) return msg.trim();

    const errField = data.error;
    if (Array.isArray(errField) && errField.length > 0) {
      return errField.map((e: any) => (typeof e === "string" ? e : e?.message ?? String(e))).join("\n");
    }
    if (typeof errField === "string" && errField.trim()) return errField.trim();

    const errors = data.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.map((e: any) => (typeof e === "string" ? e : e?.message ?? String(e))).join("\n");
    }
  }

  if (typeof data === "string" && data.trim()) return data.trim();
  return err?.message || String(err);
}

/**
 * Повертає { title, content } для Modal.error з універсальною обробкою помилок бекенду
 * (включаючи 409 Conflict та інші статуси).
 */
export function getApiErrorForModal(err: any): { title: string; content: string } {
  const response = getResponse(err);
  const status = response?.status;
  const content = extractApiError(err);

  const titleByStatus: Record<number, string> = {
    400: "Помилка валідації",
    409: "Конфлікт",
    404: "Не знайдено",
    403: "Доступ заборонено",
    422: "Помилка даних",
  };
  const title = (status != null && titleByStatus[status]) || "Помилка";
  return { title, content };
}

type JsonErr = {
  handler: string;
  field: "offerUrl" | "offerError";
  message: string;
};

function validateJsonRequired(raw: unknown): string | null {
  // обов’язкове поле
  if (typeof raw !== "string")
    return "Поле обов’язкове і не може бути порожнім";
  const s = raw.trim();
  if (!s) return "Поле обов’язкове і не може бути порожнім";

  try {
    const parsed = JSON.parse(s);

    // Забороняємо просто JSON-рядок - має бути об'єкт або масив
    if (typeof parsed === "string") {
      if (parsed.trim().length === 0) {
        return "Порожній рядок не дозволений. Введіть валідний JSON-об'єкт або масив.";
      }
      return "Має бути JSON-об'єкт або масив, а не просто рядок. Використовуйте формат: {...} або [...]";
    }

    // Перевіряємо, що це об'єкт або масив
    if (typeof parsed !== "object" || parsed === null) {
      return "Має бути JSON-об'єкт або масив, а не примітивне значення. Використовуйте формат: {...} або [...]";
    }

    return null; // валідно
  } catch (e: any) {
    return e?.message ? `Невалідний JSON: ${e.message}` : "Невалідний JSON";
  }
}

/** Перевіряє endpointMappings з форми на валідність JSON-полів
 *  - якщо offerResponseType === "json" або "encrypted" → перевіряємо m.offerUrl (лише якщо непорожній)
 *  - якщо offerErrorType === "json" або "encrypted" → перевіряємо m.offerError (лише якщо непорожній)
 *  - якщо offerErrorType === "render" → рекомендуємо (не обов’язково) мати errorStaticSiteId
 */
export function validateEndpointMappingsForJson(mappings: any[]): JsonErr[] {
  const errors: JsonErr[] = [];

  for (const m of mappings) {
    const h = String(m?.handler ?? "");

    if (
      m?.offerResponseType === "json" ||
      m?.offerResponseType === "encrypted" ||
      m?.offerResponseType === "websocket"
    ) {
      const msg = validateJsonRequired(m?.offerUrl);
      if (msg) errors.push({ handler: h, field: "offerUrl", message: msg });
    }

    if (m?.offerErrorType === "json" || m?.offerErrorType === "encrypted") {
      const msg = validateJsonRequired(m?.offerError);
      if (msg) errors.push({ handler: h, field: "offerError", message: msg });
    }
  }

  return errors;
}

/**
 * Очищає offer-поля з mapping об'єкта (встановлює їх в undefined)
 * Використовується для не оферних функцій
 */
export function clearOfferFields(mapping: EndpointMapping): EndpointMapping {
  return {
    ...mapping,
    offerUrl: undefined,
    offerError: undefined,
    offerErrorCode: undefined,
    offerErrorType: undefined,
    offerResponseCode: undefined,
    offerResponseType: undefined,
    encryptionAlgorithm: undefined,
    encryptionKey: undefined,
    errorLandingSource: undefined,
    errorStaticSiteId: undefined,
    errorLandingIndexPath: undefined,
  };
}

export function inflateFromApi(m: EndpointMapping): EndpointMapping {
  const ui: EndpointMapping = { ...m };

  if (m.offerResponseType === "json" || m.offerResponseType === "websocket") {
    if (typeof m.offerUrl === "string" && m.offerUrl.trim()) {
      try {
        ui.offerUrl = JSON.stringify(JSON.parse(m.offerUrl), null, 2);
      } catch {
        ui.offerUrl = String(m.offerUrl);
      }
    } else {
      ui.offerUrl = "";
    }
  } else {
    ui.offerUrl =
      typeof m.offerUrl === "string" && m.offerUrl.trim()
        ? m.offerUrl
        : "{{OFFER_URL}}";
  }
  if (m.offerErrorType === "json") {
    if (typeof m.offerError === "string" && m.offerError.trim()) {
      try {
        ui.offerError = JSON.stringify(JSON.parse(m.offerError), null, 2);
      } catch {
        ui.offerError = String(m.offerError);
      }
    } else {
      ui.offerError = "";
    }
  } else if (m.offerErrorType === "render") {
    const hasRaw =
      Boolean(m.errorStaticSiteId && m.errorStaticSiteId.trim()) ||
      Boolean(m.errorLandingIndexPath && m.errorLandingIndexPath.trim());
    if (hasRaw) {
      ui.errorStaticSiteId = m.errorStaticSiteId || "";
      ui.errorLandingIndexPath = m.errorLandingIndexPath || "";
    } else if (typeof m.offerError === "string" && m.offerError.trim()) {
      try {
        const parsed = JSON.parse(m.offerError);
        ui.errorStaticSiteId = parsed.staticSiteId ?? "";
        ui.errorLandingIndexPath = parsed.indexPath ?? "";
      } catch {}
    }
    if (!ui.errorLandingIndexPath) ui.errorLandingIndexPath = "index.html";
    ui.offerError = "";
  } else {
    ui.offerError =
      typeof m.offerError === "string" && m.offerError.trim()
        ? m.offerError
        : "{{OFFER_ERROR}}";
  }
  return ui;
}
